import type { ICountries, ICountryCode as Props } from '.';

import { useEffect, useState } from 'react';
import Select, { components, SingleValueProps, StylesConfig } from 'react-select';

import countries from 'json/country-codes.json';

import './country-code.scss';

/**
 * Confirmation Modal component
 *
 * @param {(e: ICountries) => void} Props.handleChangeCountry - Method to change Country code
 * @param {string} Props.countryCode - selected country code
 * @param {string} Props.optionsClassName - className for options dropdown
 * @param {string} Props.className - className for input box
 * @param {boolean} Props.isDisabled - disable country code dropdown
 *
 */

export const CountryCode = ({
	handleChangeCountry,
	countryCode = '+1',
	optionsClassName = '',
	className = '',
	isDisabled = false,
}: Props) => {
	const [country, setCountry] = useState<ICountries>();

	/*
	 * react-select paints the menu from JS, so no stylesheet reaches it and its
	 * defaults are a WHITE surface with `color: inherit` rows. Inside a dark form
	 * that inherited ink is light, which is how the list came to be near-white
	 * country names on a white sheet — every row unreadable except the selected
	 * one, whose white-on-blue react-select sets itself.
	 *
	 * Tokens, so the menu follows whichever dark scope is live (the panel-wide
	 * `:root[data-theme='dark']` in main.scss, or an sv2 modal scope) and stays
	 * exactly as it was in light: --color-white IS #ffffff there, and
	 * --color-text-90-light (#2f323d) is react-select's own #333 to the eye.
	 */
	const customStyles: StylesConfig<ICountries, false> = {
		option: (styles, state) => ({
			...styles,
			cursor: 'pointer',
			boxShadow: 'none',
			/* isSelected keeps react-select's white-on-accent, which reads on either
			   theme; the other rows are what needed colours of their own — including
			   the hover, whose default #DEEBFF is a near-white band that would carry
			   light text on a dark menu. */
			color: state.isSelected ? styles.color : 'var(--color-text-90-light)',
			backgroundColor: state.isSelected
				? styles.backgroundColor
				: state.isFocused
				? 'var(--color-button-bg-light-blue)'
				: 'transparent',
		}),
		control: (styles) => ({
			...styles,
			cursor: 'pointer',
			border: 'none',
			boxShadow: 'none',
			backgroundColor: 'transparent',
			'&:hover': {
				border: 'none',
				borderRadius: '5px',
				boxShadow: 'none',
			},
		}),
		menu: (styles) => ({
			...styles,
			width: '350px', // Make the dropdown menu 100% wide
			'@media only screen and (max-width: 430px)': {
				width: '300px',
			},
			backgroundColor: 'var(--color-white)',
			border: '1px solid var(--color-input-border-light)',
		}),
		/* The closed control: the flag and dial code sit in the field itself, and
		   react-select's singleValue hardcodes near-black #333 for them. */
		singleValue: (styles) => ({
			...styles,
			color: 'var(--color-text-90-light)',
		}),
		input: (styles) => ({
			...styles,
			color: 'var(--color-text-90-light)',
		}),
	};

	useEffect(() => {
		const countryObj = countries.find(item => item.label === countryCode);
		if (countryObj && countryObj.label !== country?.label) {
			setCountry(countryObj);
		}
	}, [countryCode, country]);

	const handleCountryChange = (selectedOption: ICountries | null) => {
		if (selectedOption) {
			setCountry(selectedOption);
			handleChangeCountry(selectedOption);
		}
	};

	// Custom SingleValue component to show only the flag and country code after selection
	const SingleValue = (props: SingleValueProps<ICountries>) => {
		return (
			<components.SingleValue {...props}>
				<div className={`option ${optionsClassName}`}>
					<div className="skeleton-wrapper">
					<img
						className="option__flag"
						src={props.data.image}
						alt="country-code"
					/>
						<div className="skeleton-image"></div>
					</div>
					<span className="option__label">{props.data.label}</span>
				</div>
			</components.SingleValue>
		);
	};

	return country ? (
		<Select
			defaultValue={country}
			styles={customStyles}
			formatOptionLabel={(countryData: ICountries) => {
				const isSelected = countryData.label === country.label;
				return (
				<div className={`option ${optionsClassName}`}>
					<div className="skeleton-wrapper">
					<img
						className="option__flag"
						src={countryData.image}
						alt="country-code"
					/>
						<div className="skeleton-image"></div>
					</div>
						<span className="option__label">
							{countryData.value}
							<span className={!isSelected ? 'option__label-code' : ''}>
								{countryData.label}
							</span>
						</span>
				</div>
			)}}
			className={`country-label ${className}`}
			options={countries}
			getOptionValue={(option: ICountries) => option.value}
			onChange={handleCountryChange}
			isSearchable
			isDisabled={isDisabled}
			value={country}
			components={{
				SingleValue,
				IndicatorSeparator: () => null,
			}}
			maxMenuHeight={140}
		/>
	) : (
		<></>
	);
};
